import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  Plus,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";

const SPECIALTY_ICONS = {
  fisioterapia:
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/vj9kp8wy_Fisioterapia.svg",
  "pilates-mat":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/thxkpi1e_2.svg",
  "pilates-equipamentos":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/r5gvlzt2_Pilates%20Cl%C3%ADnico%20Equipamentos.svg",
  gyrotonic:
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/v48h1grw_Gyrotonic.svg",
  "power-plate":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/9mw1bc4j_Power%20Plate.svg",
  "terapia-fala":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/2iygfdsn_Terapia%20da%20Fala.svg",
  psicologia:
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/d426wu7e_Psicologia.svg",
  "medicina-chinesa":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/mtk7x2u5_Medicina%20Chinesa.svg",
  osteopatia:
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/70lotccm_Osteopatia.svg",
  "treino-clinico":
    "https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/uaaqmax5_Treino%20Cl%C3%ADnico%20Personalizado.svg",
};

export const Servicos = () => {
  const { t } = useT();
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId((curr) => (curr === id ? null : id));

  const getSpecialty = (id) => {
    const raw = t(`servicos.items.${id}`);
    return {
      id,
      iconUrl: SPECIALTY_ICONS[id],
      name: raw?.name || id,
      description: raw?.description || "",
      detailHref: raw?.detailHref || null,
      detailCta: raw?.detailCta || null,
    };
  };

  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-[#EEF2E9]"
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6">
        {/* ───── Header ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-24 md:mb-32 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-[#A3BD98]" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#ADBDAD]">
                {t("servicos.eyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[60px] leading-[1.04] tracking-tight text-[#2A322C]">
              <span className="block font-semibold text-[#2A322C]">
                {t("servicos.titleTop")}
              </span>
              <span className="block font-light text-[#5C6660]/85 mt-2 md:mt-3">
                {t("servicos.titleBottom")}
              </span>
            </h2>
          </div>

          <div className="lg:col-span-5 flex lg:h-full">
            <div className="lg:border-l lg:border-[#E4E9DE]/70 lg:pl-8 lg:self-center max-w-sm">
              <p className="text-[15px] md:text-base text-[#5C6660] leading-[1.9]">
                {t("servicos.intro1")}
              </p>
              <p className="text-[15px] md:text-base text-[#5C6660] leading-[1.9] mt-5">
                {t("servicos.intro2")}
              </p>
            </div>
          </div>
        </div>

        {/* ───── Groups ───── */}
        <div className="space-y-20 md:space-y-28">
          {[
            {
              id: "tratamentos",
              title: t("servicos.groupTratamentosTitle"),
              description: t("servicos.groupTratamentosDesc"),
              theme: "neutral",
              ids: [
                "fisioterapia",
                "terapia-fala",
                "psicologia",
                "medicina-chinesa",
                "osteopatia",
              ],
            },
            {
              id: "treino-movimento",
              title: t("servicos.groupTreinoTitle"),
              description: t("servicos.groupTreinoDesc"),
              theme: "energy",
              ids: [
                "pilates-mat",
                "pilates-equipamentos",
                "gyrotonic",
                "power-plate",
                "treino-clinico",
              ],
            },
          ].map((group) => {
            const groupItems = group.ids.map((id) => getSpecialty(id));
            const isEnergy = group.theme === "energy";
            const tokens = isEnergy
              ? {
                  dot: "bg-[#EE8984]",
                  rule: "bg-[#EE8984]/70",
                  topAccent: "bg-[#EE8984]",
                  cardBorder: "border-[#E4E9DE]/80",
                  cardOpenBorder: "border-[#EE8984]/25",
                  cardHoverBorder: "hover:border-[#EE8984]/35",
                  cardHoverShadow:
                    "hover:shadow-[0_24px_55px_-25px_rgba(238,137,132,0.25)]",
                  iconBg: "bg-[#EE8984]/[0.06] group-hover:bg-[#EE8984]/10",
                  iconBgOpen: "bg-[#EE8984]/10",
                  plusClosed:
                    "bg-white border border-[#E4E9DE] text-[#EE8984] group-hover:border-[#EE8984]/50 group-hover:bg-[#EE8984]/5",
                  plusOpen: "bg-[#EE8984] text-white",
                  label: "text-[#CF8376]",
                }
              : {
                  dot: "bg-[#A3BD98]",
                  rule: "bg-[#A3BD98]",
                  topAccent: "bg-[#A3BD98]",
                  cardBorder: "border-[#E4E9DE]/70",
                  cardOpenBorder: "border-[#A3BD98]/20",
                  cardHoverBorder: "hover:border-[#ADBDAD]/50",
                  cardHoverShadow:
                    "hover:shadow-[0_22px_50px_-25px_rgba(42,50,44,0.12)]",
                  iconBg: "bg-[#EEF2E9]/70 group-hover:bg-[#EEF2E9]",
                  iconBgOpen: "bg-[#A3BD98]/[0.06]",
                  plusClosed:
                    "bg-white border border-[#E4E9DE] text-[#A3BD98] group-hover:border-[#A3BD98]/50 group-hover:bg-[#A3BD98]/5",
                  plusOpen: "bg-[#A3BD98] text-white",
                  label: "text-[#ADBDAD]",
                };
            return (
              <div key={group.id} data-testid={`group-${group.id}`}>
                {/* Group header (subtle) */}
                <div className="mb-10 md:mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-10">
                  <div className="flex items-center gap-4">
                    <span className={`h-px w-8 ${tokens.rule}`} />
                    <span
                      aria-hidden
                      className={`w-[6px] h-[6px] rounded-full ${tokens.dot}`}
                    />
                    <h3 className="font-serif text-[20px] md:text-[22px] font-medium text-[#2A322C] tracking-tight">
                      {group.title}
                    </h3>
                  </div>
                  <p className="text-[13.5px] md:text-sm text-[#5C6660] leading-relaxed max-w-md md:text-right">
                    {group.description}
                  </p>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 lg:gap-7 items-start">
                  {groupItems.map((s, idx) => {
                    const isOpen = openId === s.id;
                    return (
                      <motion.article
                        key={s.id}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.55, delay: idx * 0.04 }}
                        data-testid={`specialty-card-${s.id}`}
                        layout
                        className={`group relative rounded-[20px] overflow-hidden transition-all duration-500 min-h-[200px] ${
                          isOpen
                            ? `bg-white border ${tokens.cardOpenBorder} shadow-[0_30px_80px_-30px_rgba(42,50,44,0.18)]`
                            : `bg-[#FEFEFE] border ${tokens.cardBorder} hover:-translate-y-[3px] ${tokens.cardHoverShadow} ${tokens.cardHoverBorder}`
                        }`}
                      >
                        {isOpen && (
                          <motion.span
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.5, ease: "easeOut" }}
                            className={`absolute top-0 left-0 right-0 h-[2px] origin-left ${tokens.topAccent}`}
                          />
                        )}

                        <button
                          onClick={() => toggle(s.id)}
                          data-testid={`specialty-toggle-${s.id}`}
                          aria-expanded={isOpen}
                          className="w-full h-full text-left px-7 md:px-9 py-8 md:py-10 flex items-center gap-6 min-h-[200px]"
                        >
                          <div
                            className={`shrink-0 w-[76px] h-[76px] rounded-2xl flex items-center justify-center transition-colors duration-500 ${
                              isOpen ? tokens.iconBgOpen : tokens.iconBg
                            }`}
                          >
                            {s.iconUrl ? (
                              <img
                                src={s.iconUrl}
                                alt=""
                                aria-hidden="true"
                                className="w-14 h-14 object-contain"
                              />
                            ) : (
                              <Activity
                                size={30}
                                strokeWidth={1.5}
                                className="text-[#2A322C]"
                              />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className="font-serif text-[19px] md:text-[21px] font-semibold text-[#2A322C] leading-[1.25] tracking-tight">
                              {s.name}
                            </h4>
                            <div
                              className={`mt-2 text-[10.5px] tracking-[0.26em] uppercase font-semibold ${
                                isOpen ? tokens.label : "text-[#ADBDAD]"
                              }`}
                            >
                              {isOpen ? t("servicos.fechar") : t("servicos.verMais")}
                            </div>
                          </div>

                          <div
                            className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-500 ${
                              isOpen
                                ? `${tokens.plusOpen} rotate-[135deg]`
                                : tokens.plusClosed
                            }`}
                          >
                            <Plus size={17} strokeWidth={1.5} />
                          </div>
                        </button>

                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              key="content"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{
                                duration: 0.42,
                                ease: [0.22, 1, 0.36, 1],
                              }}
                              className="overflow-hidden"
                            >
                              <div className="px-7 md:px-9 pb-10 md:pb-12">
                                <div className="h-px bg-gradient-to-r from-transparent via-[#E4E9DE] to-transparent mb-8" />

                                <p className="text-[15.5px] text-[#5C6660] leading-[1.85]">
                                  {s.description}
                                </p>

                                <div className="mt-10 pt-8 border-t border-dashed border-[#E4E9DE] flex flex-col gap-5 items-start">
                                  <p className="text-[13.5px] text-[#5C6660] italic leading-relaxed max-w-full">
                                    {t("servicos.contactCta")}
                                  </p>
                                  <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
                                    {s.detailHref && (
                                      <Link
                                        to={s.detailHref}
                                        data-testid={`specialty-detail-${s.id}`}
                                        className="group/link inline-flex items-center justify-center gap-2 border border-[#A3BD98]/55 text-[#2A322C] rounded-full px-6 py-3 text-[13px] font-semibold tracking-wide hover:bg-[#A3BD98] hover:text-white hover:border-[#A3BD98] transition-all duration-300"
                                      >
                                        {s.detailCta}
                                        <ArrowRight
                                          size={14}
                                          className="group-hover/link:translate-x-[3px] transition-transform duration-300"
                                        />
                                      </Link>
                                    )}
                                    <a
                                      href={WHATSAPP_URL}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={`specialty-cta-${s.id}`}
                                      className="group/btn inline-flex items-center justify-center gap-2 bg-[#EE8984] text-white rounded-full px-6 py-3 text-[13px] font-medium tracking-wide hover:bg-[#CF8376] transition-colors"
                                    >
                                      {t("servicos.faleConnosco")}
                                      <ArrowUpRight
                                        size={15}
                                        className="group-hover/btn:rotate-45 transition-transform duration-300"
                                      />
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.article>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
